import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { api } from "../lib/api";

const CONFETTI_COLORS = [
  "#ffd166",
  "#06d6a0",
  "#118ab2",
  "#ef476f",
  "#ffffff",
  "#f4a261",
];

function isSameUtcDay(isoA, isoB) {
  if (!isoA || !isoB) return false;
  return isoA.slice(0, 10) === isoB.slice(0, 10);
}

/**
 * Shows a same-day celebration card on the shipment confirmation page when the
 * next active departure is today AND the shipment was created today.
 * Renders nothing otherwise.
 */
export default function ShipmentCelebration({ shipmentCreatedAt }) {
  const [dep, setDep] = useState(null);

  useEffect(() => {
    api
      .get("/departures/next")
      .then((r) => setDep(r.data?.next === null ? null : r.data))
      .catch(() => setDep(null));
  }, []);

  if (!dep || dep.days_remaining !== 0) return null;
  if (!isSameUtcDay(dep.date, shipmentCreatedAt)) return null;

  const pieces = Array.from({ length: 14 });

  return (
    <div
      data-testid="envio-celebration"
      role="status"
      className="relative overflow-hidden mb-6 rounded-3xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white px-6 sm:px-8 py-6 shadow-lg shadow-emerald-900/25"
    >
      {/* CSS-only tasteful confetti (no external dep) */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        {pieces.map((_, i) => {
          const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
          const left = (i * 7 + 3) % 100;
          const delay = (i * 137) % 900;
          const dur = 1600 + ((i * 173) % 1200);
          const size = 6 + (i % 3) * 2;
          return (
            <span
              key={i}
              style={{
                position: "absolute",
                left: `${left}%`,
                top: "-16px",
                width: `${size}px`,
                height: `${size}px`,
                background: color,
                borderRadius: i % 2 === 0 ? "2px" : "50%",
                opacity: 0.9,
                animation: `mercago-confetti-fall ${dur}ms cubic-bezier(0.3, 0.7, 0.4, 1) ${delay}ms 1`,
                animationFillMode: "forwards",
                transform: "translateY(0)",
              }}
            />
          );
        })}
      </div>
      <style>{`
        @keyframes mercago-confetti-fall {
          0%   { transform: translateY(0)   rotate(0deg); opacity: 0; }
          10%  { opacity: 0.95; }
          100% { transform: translateY(220px) rotate(540deg); opacity: 0; }
        }
      `}</style>

      <div className="relative flex items-start gap-3">
        <div className="h-10 w-10 rounded-full bg-white/20 grid place-items-center shrink-0">
          <Sparkles className="h-6 w-6" />
        </div>
        <div>
          <div
            data-testid="envio-celebration-title"
            className="font-black text-lg sm:text-xl leading-tight"
          >
            ¡Tu paquete llegará en la próxima salida — hoy!
          </div>
          <p className="text-white/90 text-sm mt-1 max-w-lg">
            Nos ponemos en marcha hacia los campamentos. Te avisaremos cuando salga.
          </p>
        </div>
      </div>
    </div>
  );
}
